import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {FONT_FAMILY_PROXIMA, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import FieldError from 'ui-library/FieldError';
import DateField from 'ui-library/inputs/DateField';
import {Size} from 'ui-library/inputs/InputConstants';
import {isValidDate} from 'util/DateUtil';

interface Validation {
  fields: {studyStartDate: {error: string | null}; studyEndDate: {error: string | null}};
  isValid: boolean;
}

interface Draft {
  studyStartDate: string;
  studyEndDate: string;
}
interface ClinicalStudyDatePickerProps {
  studyStartDate: string;
  studyEndDate: string;
  setStudyStartDate: (startDate: string) => void;
  setStudyEndDate: (endDate: string) => void;
  validation: Validation;
  locked: boolean;
}

/** @package exported for testing * */
export function getDateValidation(draft: Draft): Validation {
  const hasValidStartDate = !draft.studyStartDate || isValidDate({textValue: draft.studyStartDate});
  const hasValidEndDate = !draft.studyEndDate || isValidDate({textValue: draft.studyEndDate});
  const startDateIsBeforeEndDate =
    hasValidStartDate &&
    hasValidEndDate &&
    isValidDate({textValue: draft.studyEndDate, min: draft.studyStartDate});
  return {
    fields: {
      studyStartDate: {
        error: hasValidStartDate ? null : 'Invalid date',
      },
      studyEndDate: {
        error: !hasValidEndDate
          ? 'Invalid date'
          : !startDateIsBeforeEndDate
            ? 'End date must be after start date'
            : null,
      },
    },
    isValid: hasValidStartDate && hasValidEndDate && startDateIsBeforeEndDate,
  };
}

export function useDateValidation({studyStartDate, studyEndDate}: Draft): Validation {
  return useMemo(() => getDateValidation({studyStartDate, studyEndDate}), [studyStartDate, studyEndDate]);
}

export default function ClinicalStudyDatePicker(props: ClinicalStudyDatePickerProps): JSX.Element {
  return (
    <div className={css(styles.dates)}>
      <div>
        <DateField
          size={Size.SMALL}
          label="Study start date"
          onChange={props.setStudyStartDate}
          value={props.studyStartDate}
          placeholder="from"
          className={css(styles.dateTimeField)}
          // TODO(BNCH-107175): Use readOnly instead of isDisabled to avoid greying out the selected date.
          isDisabled={props.locked}
        />
        {!props.validation.isValid && <FieldError message={props.validation.fields.studyStartDate.error} />}
      </div>
      <span className={css(styles.dateDash)}>-</span>
      <div>
        <DateField
          size={Size.SMALL}
          label="Study end date"
          onChange={props.setStudyEndDate}
          value={props.studyEndDate}
          placeholder="to"
          className={css(styles.dateTimeField)}
          // TODO(BNCH-107175): Use readOnly instead of isDisabled to avoid greying out the selected date.
          isDisabled={props.locked}
        />
        {!props.validation.isValid && <FieldError message={props.validation.fields.studyEndDate.error} />}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  dateTimeField: {
    width: 98,
  },
  dateDash: {
    fontFamily: FONT_FAMILY_PROXIMA,
    fontSize: 34,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Color.GRAY_400,
    transform: 'translateY(7px)',
  },
  dates: {
    height: 48,
    display: 'flex',
    flexDirection: 'row',
    marginTop: 4,
    gap: '10px',
  },
  dateLabels: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});
